import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QRCode from 'react-qr-code';
import { Dayjs } from 'dayjs';
import { Team, TableTeamsRow } from './TableTeamsRow';
import { Cohort } from './TableCohortsRow';
import { CohortDetailPanel } from './CohortDetailPanel';
import { CohortDateEmailConfig, CohortEmailConfigs } from './CohortEmailDialog';
import { EmailDefaultsDialog } from './EmailDefaultsDialog';
import { Survey } from './TableSurveysRow';
import ExcelUploadAndParse, { TeamsUploadOutput } from './ExcelUploadAndParse';

interface TableTeamsProps {
  teams: Team[];
  setTeams: (teams: Team[]) => void;
  cohorts: Cohort[];
  setCohorts: (cohorts: Cohort[]) => void;
  surveys: Survey[];
  loading: boolean;
  createTeam: (name: string, leader: string) => Promise<Team>;
  editTeam: (teamId: string, name: string, leader: string) => Promise<void>;
  deleteTeam: (teamId: string, deleteSurveys: boolean) => Promise<void>;
  addSurveyToTeam: (teamId: string, surveyId: string) => Promise<void>;
  removeSurveyFromTeam: (teamId: string, surveyId: string) => Promise<void>;
  createCohort: (name: string, systemLeader: string) => Promise<Cohort>;
  editCohort: (
    cohortId: string,
    name: string,
    systemLeader: string,
    dates: string[],
    dateNames?: string[]
  ) => Promise<void>;
  deleteCohort: (cohortId: string) => Promise<void>;
  addSurveyToCohort: (cohortId: string, surveyId: string) => Promise<void>;
  removeSurveyFromCohort: (cohortId: string, surveyId: string) => Promise<void>;
  assignTeamToCohort: (teamId: string, cohortId: string) => Promise<void>;
  removeTeamFromCohort: (teamId: string) => Promise<void>;
  editCohortEmailConfig: (
    cohortId: string,
    dateIndex: string,
    config: CohortDateEmailConfig
  ) => Promise<void>;
  uploadCohortAttachment: (
    cohortId: string,
    fileName: string,
    fileType: string
  ) => Promise<{ uploadUrl: string; fileKey: string }>;
  sendCohortEmails: (
    cohortId: string,
    dateIndex: string
  ) => Promise<{ sent: number; errors: string[] }>;
  getEmailDefaults: () => Promise<CohortEmailConfigs>;
  setEmailDefault: (dateIndex: string, config: CohortDateEmailConfig) => Promise<void>;
  /** Saving global email defaults is an admin-only backend operation. */
  isAdmin: boolean;
  multipleCreateCohort: (
    cohorts: Array<unknown>,
    teamsWithoutCohort: Array<unknown>
  ) => Promise<{ cohorts: Cohort[]; teams: Team[] }>;
  analyzeSurvey: (surveyId: string | number) => void;
  loadingSurvey: string | number;
  getParticipants: (surveyId: string | number) => void;
  participantsBySurvey: { [key: string]: any };
  loadingParticipants: string | number;
  isMobile: boolean;
  editLeaderReportDate: (
    survey: Survey,
    leader: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null
  ) => void;
  getLeaderAndReportDate: (surveyId: string | number) => void;
  loadingLeader: string | number;
  leaderBySurvey: { [key: string]: any };
}

export const TableTeams: React.FC<TableTeamsProps> = ({
  teams,
  setTeams,
  cohorts,
  setCohorts,
  surveys,
  loading,
  createTeam,
  editTeam,
  deleteTeam,
  addSurveyToTeam,
  removeSurveyFromTeam,
  createCohort,
  editCohort,
  deleteCohort,
  addSurveyToCohort,
  removeSurveyFromCohort,
  assignTeamToCohort,
  removeTeamFromCohort,
  editCohortEmailConfig,
  uploadCohortAttachment,
  sendCohortEmails,
  getEmailDefaults,
  setEmailDefault,
  isAdmin,
  multipleCreateCohort,
  analyzeSurvey,
  loadingSurvey,
  getParticipants,
  participantsBySurvey,
  loadingParticipants,
  isMobile,
  editLeaderReportDate,
  getLeaderAndReportDate,
  loadingLeader,
  leaderBySurvey,
}) => {
  const { t } = useTranslation();
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = React.useState(false);
  const [createCohortDialogOpen, setCreateCohortDialogOpen] = React.useState(false);
  const [qrModalOpen, setQrModalOpen] = React.useState<string>('');
  const [newTeamName, setNewTeamName] = React.useState('');
  const [newTeamLeader, setNewTeamLeader] = React.useState('');
  const [newCohortName, setNewCohortName] = React.useState('');
  const [newCohortSystemLeader, setNewCohortSystemLeader] = React.useState('');
  const [createTeamLoading, setCreateTeamLoading] = React.useState(false);
  const [createCohortLoading, setCreateCohortLoading] = React.useState(false);
  const [emailDefaultsDialogOpen, setEmailDefaultsDialogOpen] = React.useState(false);
  const [multipleCreateLoading, setMultipleCreateLoading] = React.useState(false);
  const [createSectionOpen, setCreateSectionOpen] = React.useState(false);
  const [selectedCohortId, setSelectedCohortId] = React.useState<string | null>(null);

  // All survey IDs already assigned to any team or cohort
  const assignedSurveyIds = React.useMemo(
    () => new Set([...teams.flatMap((t) => t.surveyIds), ...cohorts.flatMap((c) => c.surveyIds)]),
    [teams, cohorts]
  );

  // Teams grouped: those with a cohort, those without
  const uncohortedTeams = React.useMemo(() => teams.filter((t) => !t.cohortId), [teams]);

  const teamsByCohort = React.useMemo(() => {
    const map = new Map<string, Team[]>();
    for (const cohort of cohorts) map.set(cohort.id, []);
    for (const team of teams) {
      if (team.cohortId && map.has(team.cohortId)) {
        map.get(team.cohortId)!.push(team);
      }
    }
    return map;
  }, [teams, cohorts]);

  const handleCreateTeam = async () => {
    if (!newTeamName) return;
    setCreateTeamLoading(true);
    try {
      const team = await createTeam(newTeamName, newTeamLeader);
      setTeams([team, ...teams]);
      setNewTeamName('');
      setNewTeamLeader('');
      setCreateTeamDialogOpen(false);
    } finally {
      setCreateTeamLoading(false);
    }
  };

  const handleCreateCohort = async () => {
    if (!newCohortName) return;
    setCreateCohortLoading(true);
    try {
      const cohort = await createCohort(newCohortName, newCohortSystemLeader);
      setCohorts([cohort, ...cohorts]);
      setSelectedCohortId(cohort.id);
      setNewCohortName('');
      setNewCohortSystemLeader('');
      setCreateCohortDialogOpen(false);
    } finally {
      setCreateCohortLoading(false);
    }
  };

  const handleEditTeam = async (team: Team, name: string, leader: string) => {
    await editTeam(team.id, name, leader);
    const leaderArray = leader
      .split(',')
      .map((e) => e.trim())
      .filter(Boolean);
    setTeams(teams.map((t) => (t.id === team.id ? { ...t, name, leader: leaderArray } : t)));
  };

  const handleDeleteTeam = async (teamId: string, deleteSurveys: boolean) => {
    await deleteTeam(teamId, deleteSurveys);
    setTeams(teams.filter((t) => t.id !== teamId));
  };

  const handleAddSurveyToTeam = async (teamId: string, surveyIds: string[]) => {
    for (const surveyId of surveyIds) await addSurveyToTeam(teamId, surveyId);
    setTeams(
      teams.map((t) => (t.id === teamId ? { ...t, surveyIds: [...t.surveyIds, ...surveyIds] } : t))
    );
  };

  const handleRemoveSurveyFromTeam = async (teamId: string, surveyId: string) => {
    await removeSurveyFromTeam(teamId, surveyId);
    setTeams(
      teams.map((t) =>
        t.id === teamId ? { ...t, surveyIds: t.surveyIds.filter((s) => s !== surveyId) } : t
      )
    );
  };

  const handleEditCohort = async (
    cohort: Cohort,
    name: string,
    systemLeader: string,
    dates: string[],
    dateNames: string[]
  ) => {
    await editCohort(cohort.id, name, systemLeader, dates, dateNames);
    const dateFields = Object.fromEntries(dates.map((d, i) => [`date${i + 1}`, d || undefined]));
    setCohorts(
      cohorts.map((c) =>
        c.id === cohort.id ? { ...c, name, systemLeader, ...dateFields, dateNames } : c
      )
    );
  };

  const handleDeleteCohort = async (cohortId: string) => {
    await deleteCohort(cohortId);
    setCohorts(cohorts.filter((c) => c.id !== cohortId));
    setTeams(teams.map((t) => (t.cohortId === cohortId ? { ...t, cohortId: undefined } : t)));
    if (selectedCohortId === cohortId) setSelectedCohortId(null);
  };

  const handleAddSurveyToCohort = async (cohortId: string, surveyIds: string[]) => {
    for (const surveyId of surveyIds) await addSurveyToCohort(cohortId, surveyId);
    setCohorts(
      cohorts.map((c) =>
        c.id === cohortId ? { ...c, surveyIds: [...c.surveyIds, ...surveyIds] } : c
      )
    );
  };

  const handleRemoveSurveyFromCohort = async (cohortId: string, surveyId: string) => {
    await removeSurveyFromCohort(cohortId, surveyId);
    setCohorts(
      cohorts.map((c) =>
        c.id === cohortId ? { ...c, surveyIds: c.surveyIds.filter((s) => s !== surveyId) } : c
      )
    );
  };

  const handleAssignTeamToCohort = async (teamId: string, cohortId: string) => {
    await assignTeamToCohort(teamId, cohortId);
    setTeams(teams.map((t) => (t.id === teamId ? { ...t, cohortId } : t)));
  };

  const handleRemoveTeamFromCohort = async (teamId: string) => {
    await removeTeamFromCohort(teamId);
    setTeams(teams.map((t) => (t.id === teamId ? { ...t, cohortId: undefined } : t)));
  };

  const handleMultipleCreate = async (data: TeamsUploadOutput) => {
    setMultipleCreateLoading(true);
    try {
      const result = await multipleCreateCohort(
        data.cohorts as unknown[],
        data.teamsWithoutCohort as unknown[]
      );
      setCohorts([...result.cohorts, ...cohorts]);
      setTeams([...result.teams, ...teams]);
    } finally {
      setMultipleCreateLoading(false);
    }
  };

  const horizontal = window.innerHeight < window.innerWidth;

  return (
    <Box>
      {/* QR modal */}
      <Dialog
        fullScreen={true}
        maxWidth={false}
        open={qrModalOpen !== ''}
        onClose={() => setQrModalOpen('')}
      >
        <div style={{ background: '#fafafa', padding: '16px' }}>
          <div
            style={{
              height: horizontal ? '88vh' : 'auto',
              margin: '0 auto',
              width: horizontal ? 'auto' : '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <QRCode
              size={256}
              style={{
                maxHeight: '100%',
                height: horizontal ? '88vh' : 'auto',
                maxWidth: '100%',
                width: horizontal ? 'auto' : '100%',
              }}
              value={qrModalOpen}
              viewBox="0 0 256 256"
            />
          </div>
        </div>
        <DialogActions>
          <Button onClick={() => setQrModalOpen('')}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <EmailDefaultsDialog
        open={emailDefaultsDialogOpen}
        onClose={() => setEmailDefaultsDialogOpen(false)}
        getDefaults={getEmailDefaults}
        onSave={setEmailDefault}
        onUploadAttachment={uploadCohortAttachment}
      />

      <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
        <Typography variant="h5" component="div" textAlign="initial">
          {t('cohorts.create')}
        </Typography>
        <IconButton
          aria-label="expand row"
          size="small"
          onClick={() => setCreateSectionOpen(!createSectionOpen)}
        >
          {createSectionOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Toolbar>
      <Collapse in={createSectionOpen} timeout="auto" unmountOnExit>
        <Paper>
          <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
            {isAdmin && (
              <Button variant="outlined" onClick={() => setEmailDefaultsDialogOpen(true)}>
                {t('cohorts.email_defaults_button')}
              </Button>
            )}
            <Button variant="outlined" onClick={() => setCreateCohortDialogOpen(true)}>
              {t('cohorts.create')}
            </Button>
            <Button variant="outlined" onClick={() => setCreateTeamDialogOpen(true)}>
              {t('teams.create')}
            </Button>
          </Box>
        </Paper>
        <ExcelUploadAndParse
          mode="teams"
          loading={multipleCreateLoading}
          onSubmit={handleMultipleCreate}
        />
      </Collapse>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }} padding={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Cohorts section — master-detail layout */}
          {cohorts.length > 0 &&
            (() => {
              const selectedCohort = cohorts.find((c) => c.id === selectedCohortId) ?? null;

              const cohortList = (
                <List disablePadding>
                  {cohorts.map((cohort) => {
                    const cohortTeams = teamsByCohort.get(cohort.id) ?? [];
                    return (
                      <React.Fragment key={cohort.id}>
                        <ListItemButton
                          selected={selectedCohortId === cohort.id}
                          onClick={() => setSelectedCohortId(cohort.id)}
                          sx={{ borderRadius: 1, py: 1 }}
                        >
                          <ListItemText
                            primary={
                              <Typography fontWeight={600} noWrap>
                                {cohort.name}
                              </Typography>
                            }
                            secondary={cohort.systemLeader || undefined}
                            slotProps={{ secondary: { noWrap: true } }}
                            sx={{ minWidth: 0, mr: 1 }}
                          />
                          <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                            <Chip
                              icon={<GroupIcon />}
                              label={cohortTeams.length}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={<AssignmentIcon />}
                              label={cohort.surveyIds.length}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </ListItemButton>
                        <Divider component="li" />
                      </React.Fragment>
                    );
                  })}
                </List>
              );

              const detailPanel = selectedCohort ? (
                <CohortDetailPanel
                  key={selectedCohort.id}
                  cohort={selectedCohort}
                  teams={teamsByCohort.get(selectedCohort.id) ?? []}
                  uncohortedTeams={uncohortedTeams}
                  allSurveys={surveys}
                  assignedSurveyIds={assignedSurveyIds}
                  onEdit={handleEditCohort}
                  onDelete={handleDeleteCohort}
                  onAddSurveytoCohort={handleAddSurveyToCohort}
                  onRemoveSurveyFromCohort={handleRemoveSurveyFromCohort}
                  onAssignTeam={(teamId) => handleAssignTeamToCohort(teamId, selectedCohort.id)}
                  onRemoveTeamFromCohort={handleRemoveTeamFromCohort}
                  onEditEmailConfig={editCohortEmailConfig}
                  onUploadAttachment={uploadCohortAttachment}
                  onSendEmails={sendCohortEmails}
                  onEditTeam={handleEditTeam}
                  onDeleteTeam={handleDeleteTeam}
                  onAddSurveyToTeam={handleAddSurveyToTeam}
                  onRemoveSurveyFromTeam={handleRemoveSurveyFromTeam}
                  analyzeSurvey={analyzeSurvey}
                  loading={loadingSurvey}
                  getParticipants={getParticipants}
                  participantsBySurvey={participantsBySurvey}
                  setQrModalOpen={setQrModalOpen}
                  loadingParticipants={loadingParticipants}
                  isMobile={isMobile}
                  editLeaderReportDate={editLeaderReportDate}
                  getLeaderAndReportDate={getLeaderAndReportDate}
                  loadingLeader={loadingLeader}
                  leaderBySurvey={leaderBySurvey}
                  onBack={isMobile ? () => setSelectedCohortId(null) : undefined}
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 200,
                  }}
                >
                  <Typography color="text.secondary">{t('cohorts.select_cohort')}</Typography>
                </Box>
              );

              if (isMobile) {
                return (
                  <>
                    <Typography variant="subtitle1" sx={{ px: 2, pt: 1, fontWeight: 600 }}>
                      {t('cohorts.title')}
                    </Typography>
                    {selectedCohortId ? (
                      <Paper
                        variant="outlined"
                        sx={{ mx: 2, mt: 1, p: 2, bgcolor: 'action.hover' }}
                      >
                        {detailPanel}
                      </Paper>
                    ) : (
                      <Paper variant="outlined" sx={{ mx: 2, mt: 1 }}>
                        {cohortList}
                      </Paper>
                    )}
                  </>
                );
              }

              return (
                <>
                  <Typography variant="subtitle1" sx={{ px: 2, pt: 1, fontWeight: 600 }}>
                    {t('cohorts.title')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', px: 2, pt: 1 }}>
                    <Paper variant="outlined" sx={{ width: 280, flexShrink: 0 }}>
                      {cohortList}
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{ flex: 1, minWidth: 0, p: 2, bgcolor: 'action.hover' }}
                    >
                      {detailPanel}
                    </Paper>
                  </Box>
                </>
              );
            })()}

          {/* Uncohorted teams section */}
          {uncohortedTeams.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                sx={{ px: 2, pt: cohorts.length > 0 ? 3 : 1, fontWeight: 600 }}
              >
                {t('teams.title')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>{t('teams.name')}</TableCell>
                      <TableCell>{t('teams.leader')}</TableCell>
                      {isAdmin && <TableCell>{t('common.owner')}</TableCell>}
                      <TableCell align="right">{t('teams.surveys')}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uncohortedTeams.map((team) => (
                      <TableTeamsRow
                        key={team.id}
                        team={team}
                        allSurveys={surveys}
                        assignedSurveyIds={assignedSurveyIds}
                        onEdit={handleEditTeam}
                        onDelete={handleDeleteTeam}
                        onAddSurvey={handleAddSurveyToTeam}
                        onRemoveSurvey={handleRemoveSurveyFromTeam}
                        analyzeSurvey={analyzeSurvey}
                        loading={loadingSurvey}
                        getParticipants={getParticipants}
                        participantsBySurvey={participantsBySurvey}
                        setQrModalOpen={setQrModalOpen}
                        loadingParticipants={loadingParticipants}
                        isMobile={isMobile}
                        editLeaderReportDate={editLeaderReportDate}
                        getLeaderAndReportDate={getLeaderAndReportDate}
                        loadingLeader={loadingLeader}
                        leaderBySurvey={leaderBySurvey}
                        isAdmin={isAdmin}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {cohorts.length === 0 && uncohortedTeams.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
              {t('teams.no_teams')}
            </Typography>
          )}
        </>
      )}

      {/* Create team dialog */}
      <Dialog open={createTeamDialogOpen} onClose={() => setCreateTeamDialogOpen(false)}>
        <DialogTitle>{t('teams.create')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 320 }}>
            <Input
              value={newTeamName}
              placeholder={t('teams.name')}
              onChange={(e) => setNewTeamName(e.target.value)}
            />
            <FormControl>
              <Input
                value={newTeamLeader}
                placeholder={t('teams.leader')}
                onChange={(e) => setNewTeamLeader(e.target.value)}
              />
              <FormHelperText>{t('teams.leader_hint')}</FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTeamDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreateTeam} disabled={createTeamLoading || !newTeamName}>
            {createTeamLoading ? <CircularProgress size={20} /> : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create cohort dialog */}
      <Dialog open={createCohortDialogOpen} onClose={() => setCreateCohortDialogOpen(false)}>
        <DialogTitle>{t('cohorts.create')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 320 }}>
            <Input
              value={newCohortName}
              placeholder={t('cohorts.name')}
              onChange={(e) => setNewCohortName(e.target.value)}
            />
            <Input
              value={newCohortSystemLeader}
              placeholder={t('cohorts.system_leader')}
              onChange={(e) => setNewCohortSystemLeader(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCohortDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreateCohort} disabled={createCohortLoading || !newCohortName}>
            {createCohortLoading ? <CircularProgress size={20} /> : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
